"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildSurveyObjectBoxClassName = exports.getInterviewSearchResultClassName = exports.getInterviewListRowClassName = exports.getReviewDecisionStatusBoxClass = exports.getInheritedStatusForDisplay = exports.isReviewStatusApprovedForDisplay = exports.isReviewStatusRejectedForDisplay = exports.getReviewDecisionStatusForObject = exports.isReviewableObjectType = void 0;
/*
 * Copyright Polytechnique Montreal and contributors
 *
 * This file is licensed under the MIT License.
 * License text available at https://opensource.org/licenses/MIT
 */
const project_config_1 = __importDefault(require("evolution-common/lib/config/project.config"));
/**
 * Whether approve/reject controls should appear for this object type in the admin review UI.
 * @param objectType - Survey object type key
 * @returns True when the survey config lists this type as reviewable
 */
const isReviewableObjectType = (objectType) => project_config_1.default.reviewableSurveyObjects.includes(objectType);
exports.isReviewableObjectType = isReviewableObjectType;
/**
 * Looks up aggregated review status for one survey object.
 * @param reviewDecisionStatusByObject - Review status grouped by object type
 * @param objectType - Survey object type key
 * @param objectUuid - Survey object uuid
 * @returns Review status for the object, if any
 */
const getReviewDecisionStatusForObject = (reviewDecisionStatusByObject, objectType, objectUuid) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    if (!reviewDecisionStatusByObject || !objectUuid) {
        return undefined;
    }
    switch (objectType) {
        case 'interview': {
            const status = reviewDecisionStatusByObject.interview;
            return (status === null || status === void 0 ? void 0 : status.objectUuid) === objectUuid ? status : undefined;
        }
        case 'household': {
            const status = reviewDecisionStatusByObject.household;
            return (status === null || status === void 0 ? void 0 : status.objectUuid) === objectUuid ? status : undefined;
        }
        case 'home': {
            const status = reviewDecisionStatusByObject.home;
            return (status === null || status === void 0 ? void 0 : status.objectUuid) === objectUuid ? status : undefined;
        }
        case 'person':
            return (_a = reviewDecisionStatusByObject.persons) === null || _a === void 0 ? void 0 : _a[objectUuid];
        case 'journey':
            return (_b = reviewDecisionStatusByObject.journeys) === null || _b === void 0 ? void 0 : _b[objectUuid];
        case 'visitedPlace':
            return (_c = reviewDecisionStatusByObject.visitedPlaces) === null || _c === void 0 ? void 0 : _c[objectUuid];
        case 'trip':
            return (_d = reviewDecisionStatusByObject.trips) === null || _d === void 0 ? void 0 : _d[objectUuid];
        case 'segment':
            return (_e = reviewDecisionStatusByObject.segments) === null || _e === void 0 ? void 0 : _e[objectUuid];
        case 'organization':
            return (_f = reviewDecisionStatusByObject.organizations) === null || _f === void 0 ? void 0 : _f[objectUuid];
        case 'vehicle':
            return (_g = reviewDecisionStatusByObject.vehicles) === null || _g === void 0 ? void 0 : _g[objectUuid];
        case 'tripChain':
            return (_h = reviewDecisionStatusByObject.tripChains) === null || _h === void 0 ? void 0 : _h[objectUuid];
        case 'junction':
            return (_j = reviewDecisionStatusByObject.junctions) === null || _j === void 0 ? void 0 : _j[objectUuid];
        case 'workPlace':
            return (_k = reviewDecisionStatusByObject.workPlaces) === null || _k === void 0 ? void 0 : _k[objectUuid];
        case 'schoolPlace':
            return (_l = reviewDecisionStatusByObject.schoolPlaces) === null || _l === void 0 ? void 0 : _l[objectUuid];
        default: {
            // Compile-time guard: fails to build if a new SurveyObjectName is not handled above.
            const exhaustiveCheck = objectType;
            return exhaustiveCheck;
        }
    }
};
exports.getReviewDecisionStatusForObject = getReviewDecisionStatusForObject;
/**
 * Whether a review status should show as rejected in the admin UI (own decision or aggregate).
 * @param status - Aggregated review status for the object
 * @returns True when the object is rejected for display purposes
 */
const isReviewStatusRejectedForDisplay = (status) => (status === null || status === void 0 ? void 0 : status.effectiveStatus) === 'rejected' || (status === null || status === void 0 ? void 0 : status.currentUserDecision) === 'reject';
exports.isReviewStatusRejectedForDisplay = isReviewStatusRejectedForDisplay;
/**
 * Whether a review status should show as approved in the admin UI (own decision or aggregate).
 * An approval the other reviewers contradict does not count: a conflict is unsettled, and
 * showing it as approved would hide the disagreement from the reviewer who approved.
 * @param status - Aggregated review status for the object
 * @returns True when the object is approved for display purposes
 */
const isReviewStatusApprovedForDisplay = (status) => (status === null || status === void 0 ? void 0 : status.effectiveStatus) === 'approved' ||
    (status === null || status === void 0 ? void 0 : status.effectiveStatus) === 'forceApproved' ||
    ((status === null || status === void 0 ? void 0 : status.currentUserDecision) === 'approve' && (status === null || status === void 0 ? void 0 : status.effectiveStatus) !== 'conflict');
exports.isReviewStatusApprovedForDisplay = isReviewStatusApprovedForDisplay;
/**
 * Review decision an object shows and passes down, from its own decision and its ancestors'.
 * A rejection anywhere in the ancestry wins, so an approved interview containing a rejected
 * person still shows that person, and everything below it, as rejected.
 * @param reviewDecisionStatusByObject - Review status grouped by object type
 * @param object - Object the decision is displayed for
 * @param object.objectType - Survey object type key
 * @param object.objectUuid - Survey object uuid
 * @param object.inheritedStatus - Decision already inherited from the ancestors
 * @returns The decision to display, or undefined when neither the object nor its ancestors were reviewed
 */
const getInheritedStatusForDisplay = (reviewDecisionStatusByObject, { objectType, objectUuid, inheritedStatus }) => {
    if (inheritedStatus === 'rejected') {
        return 'rejected';
    }
    const status = (0, exports.getReviewDecisionStatusForObject)(reviewDecisionStatusByObject, objectType, objectUuid);
    if ((0, exports.isReviewStatusRejectedForDisplay)(status)) {
        return 'rejected';
    }
    return (0, exports.isReviewStatusApprovedForDisplay)(status) ? 'approved' : inheritedStatus;
};
exports.getInheritedStatusForDisplay = getInheritedStatusForDisplay;
/**
 * CSS modifier for a survey object box from its effective review status.
 * @param status - Aggregated review status for the object
 * @returns Class name suffix for the box, or empty when not reviewed / not reviewable
 */
const getReviewDecisionStatusBoxClass = (status) => {
    if (!status || (!status.isReviewed && !status.isForceApproved)) {
        return '';
    }
    switch (status.effectiveStatus) {
        case 'rejected':
            return 'admin__survey-object-box--rejected';
        case 'approved':
        case 'forceApproved':
            return 'admin__survey-object-box--approved';
        case 'conflict':
            return 'admin__survey-object-box--conflict';
        default:
            return '';
    }
};
exports.getReviewDecisionStatusBoxClass = getReviewDecisionStatusBoxClass;
/**
 * Colour classes of an interview row in the admin lists, by interview-level
 * review status. `approved` is resolved separately, since an approved interview
 * the participant never completed still needs the reviewer's attention.
 */
const listRowClassNameByReviewStatus = {
    forceApproved: '_green _strong _active-background',
    conflict: '_yellow _strong',
    rejected: '_dark-red _strong',
    notReviewed: ''
};
/**
 * Colour classes of one interview row of the admin interview lists.
 * @param interview - Review status and completion of the interview on the row
 * @returns Space-separated CSS class names, empty when the row needs no colour
 */
const getInterviewListRowClassName = (interview) => {
    var _a;
    if (interview.reviewStatus === 'approved') {
        return interview.isCompleted ? '_dark-green _strong' : '_orange _strong';
    }
    return (_a = listRowClassNameByReviewStatus[interview.reviewStatus]) !== null && _a !== void 0 ? _a : '';
};
exports.getInterviewListRowClassName = getInterviewListRowClassName;
/**
 * Colour classes of one interview of the admin interview search results. An
 * incomplete interview shows as incomplete whatever the reviewers decided, so
 * the rejected colour only applies to completed interviews.
 * @param interview - Review status and completion of the interview
 * @returns Space-separated CSS class names, empty when the row needs no colour
 */
const getInterviewSearchResultClassName = (interview) => {
    if (interview.isCompleted !== true) {
        return '_orange _strong';
    }
    if (interview.reviewStatus === 'approved' || interview.reviewStatus === 'forceApproved') {
        return '_green _strong _active-background';
    }
    return interview.reviewStatus === 'rejected' ? '_dark-red _strong' : '';
};
exports.getInterviewSearchResultClassName = getInterviewSearchResultClassName;
/**
 * Builds class names for a reviewable survey object box in the admin column.
 * @param options - Object type, review status, layout flags, and optional gutter override
 * @returns Combined CSS class string
 */
const buildSurveyObjectBoxClassName = ({ objectType, status, extraClassNames = '', objectUuid, inheritedStatus, hasReviewControls, nested = false }) => {
    const classes = ['admin__survey-object-box'];
    if (extraClassNames) {
        classes.push(extraClassNames);
    }
    if (nested) {
        classes.push('admin__survey-object-box--nested');
    }
    const showReviewGutter = hasReviewControls !== null && hasReviewControls !== void 0 ? hasReviewControls : Boolean(objectUuid && (0, exports.isReviewableObjectType)(objectType));
    if (showReviewGutter) {
        classes.push('admin__survey-object-box--has-review');
    }
    // An inherited rejection overrides the object's own decision, while an inherited approval
    // only applies to objects nobody reviewed individually.
    const statusClass = inheritedStatus === 'rejected'
        ? 'admin__survey-object-box--rejected'
        : (0, exports.getReviewDecisionStatusBoxClass)(status) ||
            (inheritedStatus === 'approved' ? 'admin__survey-object-box--approved' : '');
    if (statusClass) {
        classes.push(statusClass);
    }
    return classes.join(' ');
};
exports.buildSurveyObjectBoxClassName = buildSurveyObjectBoxClassName;
//# sourceMappingURL=reviewDecisionStatusHelper.js.map